import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '../../../components/Button';
import { ApiError } from '../../../services/api';
import { getSampleDataTemplate, updateSampleDataTemplate } from '../../../services/sample-data';
import type { SampleDataFieldError, SampleDataTemplateContent } from '../../../types/sampleData';
import { EMPTY_SAMPLE_DATA_TEMPLATE } from '../../../types/sampleData';
import { dateTimeFormatter } from '../../../utils/dateTimeParts';
import { SampleDataErrorSummary } from './SampleDataErrorSummary';
import { SampleDataTemplateForm } from './SampleDataTemplateForm';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import './SampleDataSection.css';

type LoadState =
  | { kind: 'loading' }
  /** The API answered 404: this server predates the endpoint. Not an error worth alarming about. */
  | { kind: 'unavailable' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready' };

type SaveStatus = { kind: 'success' | 'error'; text: string } | null;

const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? `${fallback} ${err.message}` : fallback;

/**
 * Field errors are addressed by row index (`players[2].phone`), so once a row is added or removed
 * they would point at the wrong row. Comparing row counts tells structure edits from typing.
 */
const rowShape = (c: SampleDataTemplateContent): string =>
  [
    c.teams.length,
    c.players.length,
    c.calls.length,
    c.notes.length,
    c.rosterBoard?.placements.length ?? -1,
    c.events.length,
    ...c.events.map((e) => e.games.length),
  ].join(',');

const formatSavedAt = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : dateTimeFormatter(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

/**
 * System admin editor for the sample data template: the teams, players, calls, notes and events
 * that get seeded into an organization so guided tours have something real to point at.
 */
export const SampleDataSection = () => {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [content, setContent] = useState<SampleDataTemplateContent>(EMPTY_SAMPLE_DATA_TEMPLATE);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SampleDataFieldError[]>([]);
  const [status, setStatus] = useState<SaveStatus>(null);
  const [saving, setSaving] = useState(false);
  const saveAbortRef = useRef<AbortController | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    getSampleDataTemplate(controller.signal)
      .then((template) => {
        setContent(template.content);
        setUpdatedAt(template.updatedAt);
        setLoad({ kind: 'ready' });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setLoad({ kind: 'unavailable' });
          return;
        }
        setLoad({ kind: 'failed', message: describeError(err, 'Could not load the template.') });
      });
    return () => controller.abort();
  }, [reloadToken]);

  useEffect(() => () => saveAbortRef.current?.abort(), []);

  useEffect(() => {
    if (fieldErrors.length > 0) summaryRef.current?.focus();
  }, [fieldErrors]);

  const handleChange = (next: SampleDataTemplateContent) => {
    if (fieldErrors.length > 0 && rowShape(next) !== rowShape(content)) setFieldErrors([]);
    setContent(next);
    if (status?.kind === 'success') setStatus(null);
  };

  const handleRetry = () => {
    setLoad({ kind: 'loading' });
    setReloadToken((token) => token + 1);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const controller = new AbortController();
    saveAbortRef.current = controller;
    setSaving(true);
    setStatus(null);
    try {
      const result = await updateSampleDataTemplate(content, controller.signal);
      if (result.ok) {
        // The edited content stays as-is; only the saved time comes back from the server.
        setFieldErrors([]);
        setUpdatedAt(result.template.updatedAt);
        setStatus({ kind: 'success', text: 'Template saved.' });
      } else {
        setFieldErrors(result.errors);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setFieldErrors([]);
      setStatus({ kind: 'error', text: describeError(err, 'Could not save the template.') });
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  return (
    <AdminPage labelledBy="sample-data-title">
      <AdminPageHeader
        title="Sample data"
        titleId="sample-data-title"
        subtitle="The teams, players and events added to an organization when it asks for sample data."
      />

      {load.kind === 'loading' && <p className="admin-state">Loading the template...</p>}

      {load.kind === 'unavailable' && (
        <p className="admin-state" data-testid="sample-data-unavailable">
          Sample data is not available on this server yet. It will show up here once the API update
          is deployed.
        </p>
      )}

      {load.kind === 'failed' && (
        <div className="admin-error" role="alert">
          <span>{load.message}</span>
          <Button type="button" variant="secondary" onClick={handleRetry} data-testid="sample-data-retry">
            Try again
          </Button>
        </div>
      )}

      {load.kind === 'ready' && (
        <form
          className="sample-data__form"
          onSubmit={handleSubmit}
          noValidate
          aria-busy={saving || undefined}
        >
          {fieldErrors.length > 0 && <SampleDataErrorSummary ref={summaryRef} errors={fieldErrors} />}
          {status?.kind === 'error' && (
            <div className="admin-error" role="alert">
              {status.text}
            </div>
          )}

          <SampleDataTemplateForm
            value={content}
            onChange={handleChange}
            errors={fieldErrors}
          />

          <div className="sample-data__actions">
            <Button type="submit" loading={saving} data-testid="sample-data-save">
              Save template
            </Button>
            <span className="sample-data__saved-at" data-testid="sample-data-saved-at">
              {updatedAt ? `Last saved ${formatSavedAt(updatedAt)}` : 'Not saved yet'}
            </span>
            <span className="sample-data__status" role="status" data-testid="sample-data-status">
              {status?.kind === 'success' ? status.text : ''}
            </span>
          </div>
        </form>
      )}
    </AdminPage>
  );
};
